/**
 * useTenantGeoRedirect
 * --------------------------------------------------------------
 * Shared hook for tenant-scoped public routes (Explorar, Vagas, Noticias,
 * Promocoes). When the user lands on the legacy non-prefixed URL (e.g.
 * /explorar) AND the URL has no slug, redirect to /<slug>/<route>.
 *
 * Resolution priority (NEVER override user choice with geolocation):
 *   1. User's chosen município (localStorage via userChosenMunicipio)
 *   2. Cached geo lookup (sessionStorage, 30-min TTL)
 *   3. Fresh geo lookup via /api/municipios/nearest
 *
 * Preserves query string. Falls back silently to the legacy URL if no
 * candidate is available.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation as useGeoLocation } from '../context/LocationContext';
import { municipiosApi } from '../services/api';
import { getUserChosenMunicipio } from '../services/userChosenMunicipio';

const GEO_CACHE_KEY = 'tenant_geo_resolved_slug';
const GEO_CACHE_TTL_MS = 30 * 60 * 1000;

function readGeoCache() {
  try {
    const raw = sessionStorage.getItem(GEO_CACHE_KEY);
    if (!raw) return null;
    const { slug, ts } = JSON.parse(raw);
    if (!slug || !ts || Date.now() - ts > GEO_CACHE_TTL_MS) return null;
    return slug;
  } catch {
    return null;
  }
}

function writeGeoCache(slug) {
  try {
    sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify({ slug, ts: Date.now() }));
  } catch {
    /* ignore */
  }
}

/**
 * @param {object} opts
 * @param {string} opts.routeName  - 'explorar' | 'vagas' | 'noticias' | 'promocoes'
 * @param {string} opts.currentSlug - município slug already on the URL
 *                                    (skip the redirect when truthy)
 */
export function useTenantGeoRedirect({ routeName, currentSlug }) {
  const navigate = useNavigate();
  const { location } = useGeoLocation();

  useEffect(() => {
    if (currentSlug) return;
    const search = window.location.search || '';

    // 1) User's explicit choice always wins
    const chosen = getUserChosenMunicipio();
    if (chosen?.slug) {
      navigate(`/${chosen.slug}/${routeName}${search}`, { replace: true });
      return;
    }

    // 2) Recently resolved geo (same tab session)
    const cached = readGeoCache();
    if (cached) {
      navigate(`/${cached}/${routeName}${search}`, { replace: true });
      return;
    }

    // 3) Fresh geo lookup
    if (!location?.latitude || !location?.longitude) return;

    let cancelled = false;
    (async () => {
      try {
        const nearest = await municipiosApi.nearest(location.latitude, location.longitude);
        const slug = nearest?.slug || nearest?.municipio?.slug;
        if (!cancelled && slug) {
          writeGeoCache(slug);
          navigate(`/${slug}/${routeName}${search}`, { replace: true });
        }
      } catch {
        /* silent fallback */
      }
    })();
    return () => { cancelled = true; };
  }, [currentSlug, location, navigate, routeName]);
}

export default useTenantGeoRedirect;
